"use client";

import React from "react";
import { CircleAlert as AlertCircle, ArrowRight, CircleCheck as CheckCircle2, Clock, Droplet, Flame, Hammer, Layers, PhoneCall, Pipette, Search, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  badge: string;
  iconName: string;
  linkPath: string;
  responseTime: string;
}
const servicesData: ServiceItem[] = [{
  id: "emergency-plumbing",
  title: "Emergency Plumbing",
  description: "Rapid dispatch 24/7 for burst pipes, major flooding, and urgent residential emergencies across Jonesboro.",
  badge: "24/7 Priority",
  iconName: "alert",
  linkPath: "/emergency-plumbing/",
  responseTime: "30-45 min arrival"
}, {
  id: "drain-cleaning",
  title: "Drain Cleaning",
  description: "Hydro-jetting and rooter equipment to eliminate persistent clogs, slow drains, and grease buildup safely.",
  badge: "Same-Day",
  iconName: "droplet",
  linkPath: "/drain-cleaning/",
  responseTime: "Same-day service"
}, {
  id: "water-heater-repair",
  title: "Water Heater Repair",
  description: "Expert diagnostics for tankless and conventional units. Fast pilot light, element, and valve replacements.",
  badge: "Top Rated",
  iconName: "flame",
  linkPath: "/water-heater-repair/",
  responseTime: "Same-day repair"
}, {
  id: "leak-detection",
  title: "Leak Detection",
  description: "Non-invasive electronic acoustic and thermal tools to pinpoint hidden slab leaks and behind-wall moisture.",
  badge: "Non-Invasive",
  iconName: "search",
  linkPath: "/leak-detection-repair/",
  responseTime: "Precise locating"
}, {
  id: "toilet-repair",
  title: "Toilet Repair",
  description: "Fixing overflowing, running, loose, or leaking toilets. Comprehensive wax ring and flush mechanism repairs.",
  badge: "Fast Fix",
  iconName: "wrench",
  linkPath: "/toilet-repair/",
  responseTime: "Prompt response"
}, {
  id: "sewer-line-service",
  title: "Sewer Line Service",
  description: "Camera video inspections, trenchless repair options, and complete main line backup resolutions.",
  badge: "Heavy Duty",
  iconName: "layers",
  linkPath: "/sewer-line-repair/",
  responseTime: "Camera dispatch"
}, {
  id: "pipe-repair",
  title: "Pipe Repair",
  description: "Copper, PEX, and PVC pipe leak patching, partial reroutes, and complete residential repiping solutions.",
  badge: "Long-Term",
  iconName: "pipette",
  linkPath: "/pipe-repair/",
  responseTime: "Emergency ready"
}, {
  id: "faucet-repair",
  title: "Faucet Repair",
  description: "Restoring leaky kitchen, bathroom, and utility faucets, broken cartridges, and low water pressure fixtures.",
  badge: "Fixture Pro",
  iconName: "hammer",
  linkPath: "/faucet-sink-repair/",
  responseTime: "Quick turnaround"
}];
function renderServiceIcon(iconName: string) {
  switch (iconName) {
    case "alert":
      return <AlertCircle className="size-6 text-primary" />;
    case "droplet":
      return <Droplet className="size-6 text-primary" />;
    case "flame":
      return <Flame className="size-6 text-primary" />;
    case "search":
      return <Search className="size-6 text-primary" />;
    case "wrench":
      return <Wrench className="size-6 text-primary" />;
    case "layers":
      return <Layers className="size-6 text-primary" />;
    case "pipette":
      return <Pipette className="size-6 text-primary" />;
    case "hammer":
      return <Hammer className="size-6 text-primary" />;
    default:
      return <Wrench className="size-6 text-primary" />;
  }
}
export default function ServicesGrid() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="servicesgrid" data-nav="light" className="relative bg-background py-16 sm:py-24 border-b border-border/40">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-3">
              <Clock className="size-3.5 text-primary" />
              <span>Available 24 Hours A Day</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
              Plumbing Services in Jonesboro, GA
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Fast, dependable solutions for urgent emergencies and routine plumbing repairs. Our licensed local technicians arrive fully equipped to resolve your problem on the first visit.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary font-semibold">
              <Link to="/services/">
                <span>View All Services</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="default" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm">
              <a href="tel:4708479076">
                <PhoneCall className="size-4" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
          </div>
        </div>

        {/* 8-Card Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesData.map((service, i) => {
          const cardContent = <Card className="group relative h-full flex flex-col justify-between border-border/70 bg-card hover:bg-card/90 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-primary/40 rounded-xl">
                <div>
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="size-12 rounded-lg bg-secondary/80 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                        {renderServiceIcon(service.iconName)}
                      </div>
                      <Badge variant="secondary" className="text-xs font-medium bg-secondary text-secondary-foreground border-transparent px-2.5 py-0.5">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-card-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 py-0">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <CardFooter className="p-6 pt-6 mt-4 flex items-center justify-between border-t border-border/40">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-primary" />
                    <span>{service.responseTime}</span>
                  </div>
                  <span className="inline-flex items-center text-xs font-semibold text-primary group-hover:translate-x-1 transition-transform duration-200">
                    Details
                    <ArrowRight className="size-3.5 ml-1" />
                  </span>
                </CardFooter>
              </Card>;
          return <div key={service.id} data-index={i} className="h-full">
                {shouldReduceMotion ? <Link to={service.linkPath} className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl">
                    {cardContent}
                  </Link> : <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.15
            }} transition={{
              duration: 0.4,
              delay: i * 0.05,
              ease: [0.21, 0.47, 0.32, 0.98]
            }} className="h-full">
                    <Link to={service.linkPath} className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl">
                      {cardContent}
                    </Link>
                  </motion.div>}
              </div>;
        })}
        </div>

        {/* Bottom Banner */}
        <div className="mt-12 rounded-xl bg-secondary/50 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <AlertCircle className="size-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground">
                Don&apos;t see your specific plumbing issue listed?
              </p>
              <p className="text-sm text-muted-foreground">
                We handle every type of residential plumbing, water line, and drainage problem across Jonesboro.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              <Link to="/contact/">
                <span>Request Service</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}